import React, { Component } from "react";
import { Row, Col, Select } from "antd";
import { withTranslation } from "react-i18next";

import Title from "../../components/common/Title";
import { Container } from "../../components/common/Elements";
import VerticalBarChart from "../../components/charts/VerticalBarChart";
import HorizontalBarChart from "../../components/charts/HorizontalBarChart";
import LoadingSpinner from "../../components/LoadingSpinner";
import { CHART_HEIGHT } from "../../components/common/Constants";
import { fetchTargetIndicators } from "../../services/infrastructure";

const { Option } = Select;

const YEARS_COLOR = "#458FD8";
const REGION_COLOR = "#34C792";
const REGION_CHART_HEIGHT = "640px";

/**
 * Общий дашборд целевых показателей («Комфорт» / «Качество жизни»).
 * Левый график каждого индикатора — вертикальные столбцы по годам (статичен),
 * правый — горизонтальные бары по районам (зависит от выбранного года).
 * Конфигурация (сервисы + наборы id показателей) приходит пропсами.
 */
class IndicatorDashboard extends Component {
  state = {
    loading: true,
    regionLoading: false,
    indicators: {}, // id -> { name, unit }
    years: [],
    selectedYear: undefined,
    byYears: {}, // id -> [{ category, value }]
    byRegion: {}, // id -> [{ category, value }]
  };

  componentDidMount() {
    this.init();
  }

  get allIds() {
    const { pairedIds = [], singleIds = [] } = this.props;
    return [...pairedIds, ...singleIds];
  }

  init = async () => {
    const { fetchAggregate, fetchRegion, pairedIds = [] } = this.props;
    try {
      const indicatorsList = await fetchTargetIndicators();
      const indicators = {};
      (indicatorsList || []).forEach((i) => {
        indicators[i.id] = { name: i.name, unit: i.unitOfMeasurement };
      });

      const aggregates = await Promise.all(
        this.allIds.map((id) => fetchAggregate(id))
      );
      const byYears = {};
      let years = [];
      this.allIds.forEach((id, idx) => {
        const agg = aggregates[idx];
        byYears[id] = this.buildYearsData(agg);
        if (years.length === 0 && agg && agg.planningPeriods) {
          years = agg.planningPeriods.map((p) => String(p.year));
        }
      });
      const selectedYear = years[0];

      const byRegion = {};
      const regions = await Promise.all(
        pairedIds.map((id) => fetchRegion(id, selectedYear))
      );
      pairedIds.forEach((id, idx) => {
        byRegion[id] = this.buildRegionData(regions[idx]);
      });

      this.setState({
        indicators,
        years,
        selectedYear,
        byYears,
        byRegion,
        loading: false,
      });
    } catch (e) {
      this.setState({ loading: false });
    }
  };

  buildYearsData = (agg) => {
    if (!agg) return [];
    const data = [];
    if (agg.reporting2019Year != null) {
      data.push({ category: "2019", value: agg.reporting2019Year });
    }
    if (agg.plan2020Year != null) {
      data.push({ category: "2020", value: agg.plan2020Year });
    }
    (agg.planningPeriods || []).forEach((p) => {
      data.push({ category: String(p.year), value: p.value });
    });
    return data;
  };

  buildRegionData = (arr) => {
    if (!Array.isArray(arr)) return [];
    return arr.map((item) => ({
      category: item.region ? item.region.nameRu : "",
      value:
        item.planningPeriods && item.planningPeriods[0]
          ? item.planningPeriods[0].value
          : null,
    }));
  };

  handleYearChange = async (year) => {
    const { fetchRegion, pairedIds = [] } = this.props;
    this.setState({ regionLoading: true, selectedYear: year });
    try {
      const regions = await Promise.all(
        pairedIds.map((id) => fetchRegion(id, year))
      );
      const byRegion = {};
      pairedIds.forEach((id, idx) => {
        byRegion[id] = this.buildRegionData(regions[idx]);
      });
      this.setState({ byRegion, regionLoading: false });
    } catch (e) {
      this.setState({ regionLoading: false });
    }
  };

  renderYearsChart = (id) => {
    const { indicators, byYears } = this.state;
    const ind = indicators[id] || {};
    return (
      <>
        <Title>{ind.name}</Title>
        <VerticalBarChart
          id={`years-${id}`}
          data={byYears[id] || []}
          color={YEARS_COLOR}
          units={ind.unit}
          height={CHART_HEIGHT}
          ignoreAdjustWidth
          noCellSize
          responsive
        />
      </>
    );
  };

  renderRegionChart = (id) => {
    const { t } = this.props;
    const { indicators, byRegion, regionLoading } = this.state;
    const ind = indicators[id] || {};
    return (
      <>
        <Title>
          {ind.name} {t("infrastructure.byRegion")}
        </Title>
        <LoadingSpinner loading={regionLoading}>
          <HorizontalBarChart
            id={`region-${id}`}
            data={byRegion[id] || []}
            color={REGION_COLOR}
            sortReverse
            height={REGION_CHART_HEIGHT}
            responsive
          />
        </LoadingSpinner>
      </>
    );
  };

  render() {
    const {
      t,
      isMobile,
      pairedIds = [],
      singleIds = [],
      sectionTitleKey,
    } = this.props;
    const { loading, years, selectedYear } = this.state;
    const colSpan = isMobile ? 24 : 12;

    return (
      <div>
        <Row className="mb20" gutter={16}>
          <Col span={isMobile ? 24 : 6}>
            <div style={{ color: "#fff", marginBottom: 6 }}>
              {t("infrastructure.year")}
            </div>
            <Select
              value={selectedYear}
              style={{ width: "100%" }}
              placeholder={t("infrastructure.year")}
              onChange={this.handleYearChange}
            >
              {years.map((y) => (
                <Option key={y} value={y}>
                  {y}
                </Option>
              ))}
            </Select>
          </Col>
        </Row>

        {sectionTitleKey && <Title>{t(sectionTitleKey)}</Title>}

        <LoadingSpinner loading={loading}>
          {pairedIds.map((id) => (
            <Container key={id}>
              <Row gutter={16}>
                <Col span={colSpan}>{this.renderYearsChart(id)}</Col>
                <Col span={colSpan}>{this.renderRegionChart(id)}</Col>
              </Row>
            </Container>
          ))}

          {singleIds.map((id) => (
            <Container key={id}>
              <Row justify="center">
                <Col span={colSpan}>{this.renderYearsChart(id)}</Col>
              </Row>
            </Container>
          ))}
        </LoadingSpinner>
      </div>
    );
  }
}

export default withTranslation()(IndicatorDashboard);
